import { RefObject, useEffect } from 'react';

import { getFocusableElements } from '@/hooks/getFocusableElements';

/**
 * While `isActive` is true, keeps Tab / Shift+Tab focus cycling within
 * `containerRef`. This prevents keyboard focus from reaching elements sitting
 * behind a modal overlay (e.g. the dark backdrop shown when a nav dropdown is
 * open).
 *
 * The listener is attached to the container, so it only intercepts Tab presses
 * while focus is already inside the trapped region.
 */
export const useFocusTrap = (isActive: boolean, containerRef: RefObject<HTMLElement>): void => {
  useEffect(() => {
    if (!isActive) return undefined;

    const container = containerRef.current;

    if (!container) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(container);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (active === first || !container.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !container.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isActive, containerRef]);
};
